import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import {
  Settings, User, Palette, Shield, LogOut, Sun, Moon, Camera,
  Crown, BadgeCheck, Ban, Coins, Loader2, Trash2, Image as ImageIcon,
  CreditCard, Bell
} from 'lucide-react';
import { resolveMedia } from '../components/social/discovery';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_IMG = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_AVATAR = 10 * 1024 * 1024;
const MAX_COVER = 15 * 1024 * 1024;

const validateImage = (file, maxSize) => {
  if (!ALLOWED_IMG.includes(file.type)) return 'Please choose a JPG, PNG or WEBP image.';
  if (file.size > maxSize) return `Image is too large (max ${Math.round(maxSize / 1024 / 1024)}MB).`;
  return null;
};

export default function SettingsPage() {
  const { user, updateUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [proLoading, setProLoading] = useState(false);
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [coverImage, setCoverImage] = useState(user?.cover_image || '');
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [activeSection, setActiveSection] = useState('profile');

  const uploadAndSave = async (file, field) => {
    const form = new FormData();
    form.append('file', file);
    const up = await api.post('/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    const abs = up.data.url?.startsWith('/api/') ? `${BACKEND_URL}${up.data.url}` : up.data.url;
    const res = await api.put('/users/profile', { [field]: abs });
    updateUser(res.data);
    return abs;
  };

  const onPickImage = async (e, maxSize, field) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const err = validateImage(file, maxSize);
    if (err) { toast.error(err); return; }
    const isAvatar = field === 'avatar';
    isAvatar ? setUploadingAvatar(true) : setUploadingCover(true);
    try {
      const abs = await uploadAndSave(file, field);
      isAvatar ? setAvatar(abs) : setCoverImage(abs);
      toast.success(isAvatar ? 'Profile picture updated' : 'Banner updated');
    } catch (err2) {
      toast.error(err2.response?.data?.detail || 'Upload failed');
    } finally {
      isAvatar ? setUploadingAvatar(false) : setUploadingCover(false);
    }
  };

  const removeImage = async (field) => {
    const isAvatar = field === 'avatar';
    try {
      const res = await api.put('/users/profile', { [field]: '' });
      updateUser(res.data);
      isAvatar ? setAvatar('') : setCoverImage('');
      toast.success(isAvatar ? 'Profile picture removed' : 'Banner removed');
    } catch {
      toast.error('Could not remove image');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await api.put('/users/profile', { display_name: displayName, bio });
      updateUser(response.data);
      toast.success('Profile updated successfully');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchasePremium = async (tier = 'standard') => {
    tier === 'pro' ? setProLoading(true) : setPremiumLoading(true);
    try {
      const response = await api.post('/premium/purchase', { tier, origin_url: window.location.origin });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start upgrade');
      setPremiumLoading(false);
      setProLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const sections = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'account', label: 'Account', icon: Shield },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'subscription', label: 'Subscription', icon: Crown },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 min-[900px]:ml-60 pb-8" data-testid="settings-page">
      {/* Header */}
      <div className="py-5">
        <h1 className="font-heading text-xl font-bold flex items-center gap-2">
          <Settings className="w-5 h-5 text-[hsl(var(--mg-purple))]" />
          Settings
        </h1>
      </div>

      <div className="flex gap-6">
        {/* Section Nav - Desktop */}
        <nav className="hidden md:flex flex-col w-48 shrink-0 space-y-0.5">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                activeSection === s.id
                  ? 'bg-[hsl(var(--mg-purple)/0.12)] text-foreground font-semibold border border-[hsl(var(--mg-rune)/0.3)]'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
              }`}
              data-testid={`settings-nav-${s.id}`}
            >
              <s.icon className="w-4 h-4" />
              {s.label}
            </button>
          ))}
        </nav>

        {/* Section Nav - Mobile (horizontal scroll) */}
        <div className="md:hidden w-full mb-4 -mx-4 px-4 overflow-x-auto flex gap-2 pb-2">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-colors ${
                activeSection === s.id
                  ? 'bg-[hsl(var(--mg-purple)/0.12)] text-foreground font-semibold border border-[hsl(var(--mg-rune)/0.3)]'
                  : 'text-muted-foreground border border-transparent hover:bg-muted/40'
              }`}
            >
              <s.icon className="w-3.5 h-3.5" />
              {s.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* --- Profile Section --- */}
          {activeSection === 'profile' && (
            <section className="mg-card-elevated p-6" data-testid="settings-profile-section">
              <h2 className="font-heading font-bold text-base mb-5 flex items-center gap-2">
                <User className="w-4 h-4 text-[hsl(var(--mg-purple))]" />
                Profile
              </h2>

              <div className="space-y-5">
                <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onPickImage(e, MAX_AVATAR, 'avatar')} data-testid="settings-avatar-input" />
                <input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onPickImage(e, MAX_COVER, 'cover_image')} data-testid="settings-cover-input" />

                {/* Banner */}
                <div>
                  <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">Banner</Label>
                  <div className="relative h-28 rounded-lg overflow-hidden bg-gradient-to-br from-[hsl(var(--mg-purple)/0.2)] to-[hsl(var(--accent)/0.1)] border border-[hsl(var(--mg-steel)/0.2)]" data-testid="settings-banner-preview">
                    {coverImage && <img src={resolveMedia(coverImage)} alt="Banner" className="w-full h-full object-cover" />}
                    {!coverImage && <div className="absolute inset-0 flex items-center justify-center text-muted-foreground"><ImageIcon className="w-7 h-7" /></div>}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button type="button" size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover} data-testid="settings-change-banner-btn">
                      {uploadingCover ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Uploading</> : <><Camera className="w-3.5 h-3.5 mr-1.5" />{coverImage ? 'Change' : 'Upload'}</>}
                    </Button>
                    {coverImage && (
                      <Button type="button" size="sm" variant="ghost" className="rounded-lg text-xs text-destructive" onClick={() => removeImage('cover_image')} disabled={uploadingCover} data-testid="settings-remove-banner-btn">
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />Remove
                      </Button>
                    )}
                  </div>
                </div>

                {/* Avatar */}
                <div>
                  <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">Profile Picture</Label>
                  <div className="flex items-center gap-5">
                    <Avatar className="w-16 h-16 border border-[hsl(var(--mg-steel)/0.25)]">
                      <AvatarImage src={resolveMedia(avatar)} className="object-cover" />
                      <AvatarFallback className="text-xl">{displayName?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => avatarInputRef.current?.click()} disabled={uploadingAvatar} data-testid="settings-change-avatar-btn">
                        {uploadingAvatar ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Uploading</> : <><Camera className="w-3.5 h-3.5 mr-1.5" />{avatar ? 'Change' : 'Upload'}</>}
                      </Button>
                      {avatar && (
                        <Button type="button" size="sm" variant="ghost" className="rounded-lg text-xs text-destructive" onClick={() => removeImage('avatar')} disabled={uploadingAvatar} data-testid="settings-remove-avatar-btn">
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Display Name */}
                <div>
                  <Label htmlFor="displayName" className="text-xs uppercase tracking-wider text-muted-foreground">Display Name</Label>
                  <Input id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your display name" className="mt-1 mg-input" data-testid="display-name-input" />
                </div>

                {/* Bio */}
                <div>
                  <Label htmlFor="bio" className="text-xs uppercase tracking-wider text-muted-foreground">Bio</Label>
                  <Textarea id="bio" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell us about yourself..." className="mt-1 mg-input resize-none" rows={3} data-testid="bio-input" />
                  <p className="text-xs text-muted-foreground mt-1">{bio.length}/160</p>
                </div>

                <button onClick={handleSave} disabled={loading} className="mg-btn mg-btn-primary text-sm" data-testid="save-profile-btn">
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </section>
          )}

          {/* --- Account Section --- */}
          {activeSection === 'account' && (
            <section className="mg-card-elevated p-6" data-testid="settings-account-section">
              <h2 className="font-heading font-bold text-base mb-5 flex items-center gap-2">
                <Shield className="w-4 h-4 text-[hsl(var(--mg-purple))]" />
                Account
              </h2>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-[hsl(var(--mg-steel)/0.12)]">
                  <div>
                    <p className="font-medium text-sm">Email</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-[hsl(var(--mg-steel)/0.12)]">
                  <div>
                    <p className="font-medium text-sm">Username</p>
                    <p className="text-xs text-muted-foreground">@{user?.username}</p>
                  </div>
                </div>
                {user?.wallet_address && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-[hsl(var(--mg-steel)/0.12)]">
                    <div>
                      <p className="font-medium text-sm">Wallet Address</p>
                      <p className="text-xs text-muted-foreground font-mono truncate max-w-[200px]">{user?.wallet_address}</p>
                    </div>
                  </div>
                )}
                <div className="pt-4 border-t border-[hsl(var(--mg-steel)/0.15)]">
                  <button onClick={handleLogout} className="mg-btn mg-btn-destructive text-sm" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Log Out
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* --- Appearance Section --- */}
          {activeSection === 'appearance' && (
            <section className="mg-card-elevated p-6" data-testid="settings-appearance-section">
              <h2 className="font-heading font-bold text-base mb-5 flex items-center gap-2">
                <Palette className="w-4 h-4 text-[hsl(var(--mg-purple))]" />
                Appearance
              </h2>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                  <div>
                    <p className="font-medium text-sm">Dark Mode</p>
                    <p className="text-xs text-muted-foreground">Toggle between dark and light themes</p>
                  </div>
                </div>
                <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="theme-switch" />
              </div>
            </section>
          )}

          {/* --- Notifications Section --- */}
          {activeSection === 'notifications' && (
            <section className="mg-card-elevated p-6" data-testid="settings-notifications-section">
              <h2 className="font-heading font-bold text-base mb-5 flex items-center gap-2">
                <Bell className="w-4 h-4 text-[hsl(var(--mg-purple))]" />
                Notifications
              </h2>
              <p className="text-sm text-muted-foreground">
                Notification preferences will be available in a future update. Currently, all notifications are enabled by default.
              </p>
              <Link to="/notifications" className="mg-btn mg-btn-ghost text-sm mt-4 inline-flex border border-[hsl(var(--mg-steel)/0.2)]">
                <Bell className="w-4 h-4 mr-2" /> View All Notifications
              </Link>
            </section>
          )}

          {/* --- Subscription Section --- */}
          {activeSection === 'subscription' && (
            <section className="space-y-5" data-testid="settings-subscription-section">
              {/* Current Status */}
              {user?.is_pro && (
                <div className="mg-card-elevated p-5 border-[hsl(var(--mg-purple)/0.3)]">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[hsl(var(--mg-purple))] to-[hsl(var(--accent))] flex items-center justify-center">
                      <Crown className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-sm flex items-center gap-2">
                        Pro Member <BadgeCheck className="w-4 h-4 text-[hsl(var(--mg-purple))] fill-[hsl(var(--mg-purple))]" />
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Ad-free &middot; 1,000 credits/month &middot; {user?.pro_months_active >= 3 ? 'Cash out eligible' : `${3 - (user?.pro_months_active || 0)} months until cash out`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {user?.is_premium && !user?.is_pro && (
                <div className="mg-card-elevated p-5 border-[hsl(var(--mg-gold-deep)/0.3)]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[hsl(var(--mg-gold))] to-[hsl(var(--mg-gold-deep))] flex items-center justify-center">
                        <Crown className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-sm flex items-center gap-2">Standard Member <BadgeCheck className="w-4 h-4 text-[hsl(var(--mg-purple))]" /></h3>
                        <p className="text-xs text-muted-foreground">&euro;1/month &middot; 100 monthly credits</p>
                      </div>
                    </div>
                    <button onClick={() => handlePurchasePremium('pro')} disabled={proLoading} className="mg-btn mg-btn-primary text-xs px-4 py-2">
                      Upgrade to Pro
                    </button>
                  </div>
                </div>
              )}

              {/* Upgrade Cards (only if not premium) */}
              {!user?.is_premium && !user?.is_pro && (
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Standard */}
                  <div className="mg-card-elevated p-5">
                    <div className="text-center mb-4">
                      <h3 className="font-heading font-bold text-sm">Standard</h3>
                      <div className="text-2xl font-bold text-[hsl(var(--mg-gold))] my-2">&euro;1<span className="text-xs text-muted-foreground">/month</span></div>
                    </div>
                    <ul className="space-y-2 text-xs mb-5">
                      <li className="flex items-center gap-2"><Coins className="w-3.5 h-3.5 text-[hsl(var(--mg-gold))]" />100 monthly credits</li>
                      <li className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5 text-[hsl(var(--mg-purple))]" />Verified badge</li>
                    </ul>
                    <button onClick={() => handlePurchasePremium('standard')} disabled={premiumLoading} className="mg-btn mg-btn-gold w-full text-sm py-2.5" data-testid="upgrade-standard-btn">
                      {premiumLoading ? 'Processing...' : 'Get Standard'}
                    </button>
                  </div>

                  {/* Pro */}
                  <div className="mg-card-elevated p-5 border-[hsl(var(--mg-purple)/0.35)] relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-[hsl(var(--mg-purple))] text-white text-[9px] px-2.5 py-0.5 rounded-bl-lg font-bold uppercase tracking-wider">Best Value</div>
                    <div className="text-center mb-4">
                      <h3 className="font-heading font-bold text-sm">Pro</h3>
                      <div className="text-2xl font-bold text-[hsl(var(--mg-purple))] my-2">&euro;9<span className="text-xs text-muted-foreground">/month</span></div>
                    </div>
                    <ul className="space-y-2 text-xs mb-5">
                      <li className="flex items-center gap-2"><Ban className="w-3.5 h-3.5 text-green-500" />Ad-free experience</li>
                      <li className="flex items-center gap-2"><Coins className="w-3.5 h-3.5 text-[hsl(var(--mg-gold))]" />1,000 monthly credits</li>
                      <li className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5 text-[hsl(var(--mg-purple))]" />Pro badge</li>
                      <li className="flex items-center gap-2"><CreditCard className="w-3.5 h-3.5 text-[hsl(var(--mg-gold))]" />Cash out (after 3mo)</li>
                    </ul>
                    <button onClick={() => handlePurchasePremium('pro')} disabled={proLoading} className="mg-btn mg-btn-primary w-full text-sm py-2.5" data-testid="upgrade-pro-btn">
                      {proLoading ? 'Processing...' : 'Go Pro'}
                    </button>
                  </div>
                </div>
              )}

              {/* Wallet link */}
              <div className="mg-card-elevated p-4">
                <Link to="/wallet" className="flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <Coins className="w-5 h-5 text-[hsl(var(--mg-gold))]" />
                  <div>
                    <p className="font-medium text-foreground">Runegold Wallet</p>
                    <p className="text-xs">Buy Runegold, view transactions, and manage your balance</p>
                  </div>
                </Link>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
